import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/input'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import {
    calcSuperheatSubcooling,
    type PressureUnit,
    type TempUnit,
    type MeteringDevice,
    type StatusLevel,
} from '../../util/superheat-subcooling.js'
import { PT_CHARTS } from '../../data/pt-charts.js'
import { DEG } from '../../constants.js'

const REFRIGERANT_IDS = Object.keys(PT_CHARTS)

const refrigerantId = signal(REFRIGERANT_IDS[0])
const suctionPressure = signal<string>('')
const suctionLineTemp = signal<string>('')
const liquidPressure = signal<string>('')
const liquidLineTemp = signal<string>('')
const pressureUnit = signal<PressureUnit>('psig')
const tempUnit = signal<TempUnit>('f')
const meteringDevice = signal<MeteringDevice>('txv')
const submitted = signal(false)
/* Bumped on reset to remount the substrate-input web components, which
   do not sync a programmatic value change back to their inner input. */
const formNonce = signal(0)

const result = computed(() => {
    if (!submitted.value) return null
    const sp = parseFloat(suctionPressure.value)
    const st = parseFloat(suctionLineTemp.value)
    const lp = parseFloat(liquidPressure.value)
    const lt = parseFloat(liquidLineTemp.value)
    if (isNaN(sp) || isNaN(st) || isNaN(lp) || isNaN(lt)) return null
    return calcSuperheatSubcooling({
        refrigerantId: refrigerantId.value,
        suctionPressure: sp,
        suctionLineTemp: st,
        liquidPressure: lp,
        liquidLineTemp: lt,
        pressureUnit: pressureUnit.value,
        tempUnit: tempUnit.value,
        meteringDevice: meteringDevice.value,
    })
})

function handleSubmit (e:Event) {
    e.preventDefault()
    submitted.value = true
}

function handleReset (e:Event) {
    /* substrate-button does not forward type="button", so the inner button
       defaults to submit. Cancel the implicit form submission here. */
    e.preventDefault()
    batch(() => {
        refrigerantId.value = REFRIGERANT_IDS[0]
        suctionPressure.value = ''
        suctionLineTemp.value = ''
        liquidPressure.value = ''
        liquidLineTemp.value = ''
        pressureUnit.value = 'psig'
        tempUnit.value = 'f'
        meteringDevice.value = 'txv'
        submitted.value = false
        formNonce.value += 1
    })
}

const STATUS_LABELS:Record<StatusLevel, string> = {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
}

export const SuperheatSubcoolingCalculator:FunctionComponent<{
    state:AppState
}> = function SuperheatSubcoolingCalculator () {
    const r = result.value
    const nonce = formNonce.value
    const tUnit = tempUnit.value === 'f' ? DEG + 'F' : DEG + 'C'
    const pUnit = pressureUnit.value.toUpperCase()

    return html`
        <${ToolPage}
            title="Superheat & Subcooling Calculator"
            description="Enter gauge pressures and line temperatures to compute
                evaporator superheat and condenser subcooling, then get a
                metering-device-aware charging diagnosis."
        >
            <${ToolWorkbench}
                emptyHint="Enter the gauge pressures and line temperatures,
                    then select Calculate."
            >
                <form
                class="tool-form"
                onSubmit=${handleSubmit}
                novalidate
            >

                <div class="tool-form-section">
                    <p class="tool-section-label">Settings</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label
                                class="tool-field-label"
                                for="ssc-refrigerant"
                            >
                                Refrigerant
                            </label>
                            <select
                                id="ssc-refrigerant"
                                class="tool-select"
                                value=${refrigerantId.value}
                                onChange=${(e:Event) => {
                                    refrigerantId.value =
                                        (e.target as HTMLSelectElement).value
                                }}
                            >
                                ${REFRIGERANT_IDS.map(id =>
                                    html`<option key=${id} value=${id}>
                                        ${id}
                                    </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <label
                                class="tool-field-label"
                                for="ssc-pressure-unit"
                            >
                                Pressure Units
                            </label>
                            <select
                                id="ssc-pressure-unit"
                                class="tool-select"
                                value=${pressureUnit.value}
                                onChange=${(e:Event) => {
                                    pressureUnit.value =
                                        (e.target as HTMLSelectElement)
                                            .value as PressureUnit
                                }}
                            >
                                <option value="psig">PSIG</option>
                                <option value="psi">PSI (absolute)</option>
                                <option value="bar">bar (gauge)</option>
                                <option value="kpa">kPa (gauge)</option>
                            </select>
                        </div>

                        <div class="tool-field">
                            <label
                                class="tool-field-label"
                                for="ssc-temp-unit"
                            >
                                Temperature Units
                            </label>
                            <select
                                id="ssc-temp-unit"
                                class="tool-select"
                                value=${tempUnit.value}
                                onChange=${(e:Event) => {
                                    tempUnit.value =
                                        (e.target as HTMLSelectElement)
                                            .value as TempUnit
                                }}
                            >
                                <option value="f">${DEG}F</option>
                                <option value="c">${DEG}C</option>
                            </select>
                        </div>

                        <div class="tool-field">
                            <label
                                class="tool-field-label"
                                for="ssc-metering-device"
                            >
                                Metering Device
                            </label>
                            <select
                                id="ssc-metering-device"
                                class="tool-select"
                                value=${meteringDevice.value}
                                onChange=${(e:Event) => {
                                    meteringDevice.value =
                                        (e.target as HTMLSelectElement)
                                            .value as MeteringDevice
                                }}
                            >
                                <option value="txv">TXV / EEV</option>
                                <option value="fixed">
                                    Fixed Orifice / Piston
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Suction Side (Low Side)</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <substrate-input
                                key=${`suctionPressure-${nonce}`}
                                label=${`Suction Pressure (${pUnit})`}
                                type="number"
                                inputmode="decimal"
                                step="any"
                                placeholder="e.g. 153"
                                value=${suctionPressure.value}
                                onInput=${(e:Event) => {
                                    suctionPressure.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`suctionLineTemp-${nonce}`}
                                label=${`Suction Line Temperature (${tUnit})`}
                                type="number"
                                inputmode="decimal"
                                step="any"
                                placeholder="e.g. 45"
                                value=${suctionLineTemp.value}
                                onInput=${(e:Event) => {
                                    const input = e.target as HTMLInputElement
                                    suctionLineTemp.value = input.value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Liquid Side (High Side)</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <substrate-input
                                key=${`liquidPressure-${nonce}`}
                                label=${`Liquid Pressure (${pUnit})`}
                                type="number"
                                inputmode="decimal"
                                step="any"
                                placeholder="e.g. 400"
                                value=${liquidPressure.value}
                                onInput=${(e:Event) => {
                                    liquidPressure.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`liquidLineTemp-${nonce}`}
                                label=${`Liquid Line Temperature (${tUnit})`}
                                type="number"
                                inputmode="decimal"
                                step="any"
                                placeholder="e.g. 90"
                                value=${liquidLineTemp.value}
                                onInput=${(e:Event) => {
                                    liquidLineTemp.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Calculate
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${handleReset}
                    >
                        Reset
                    </substrate-button>
                </div>
            </form>

            ${submitted.value && r === null && html`
                <div class="tool-readout" data-status="exceeds">
                    <p class="tool-readout-label">No result</p>
                    <p class="tool-readout-detail">
                        No PT data for the selected refrigerant at the given
                        pressures. Check that your readings are within the
                        supported range.
                    </p>
                </div>
            `}

            ${r && html`
                <div class="tool-readout">
                    <div class="tool-metrics">
                        <div class="tool-metric">
                            <p class="tool-metric-label">Superheat</p>
                            <p class="tool-metric-value">
                                ${r.superheat.value.toFixed(1)}${DEG}F
                            </p>
                            <p class="tool-readout-detail">
                                Evap sat temp:
                                ${r.evapSatTemp.toFixed(1)}${DEG}F
                            </p>
                            <p class="tool-status" data-status=${
                                r.superheat.status === 'normal' ?
                                    'compliant' :
                                    'exceeds'
                            }>
                                ${STATUS_LABELS[r.superheat.status]}
                            </p>
                            <p class="tool-readout-detail">
                                ${r.superheat.recommendation}
                            </p>
                        </div>

                        <div class="tool-metric">
                            <p class="tool-metric-label">Subcooling</p>
                            <p class="tool-metric-value">
                                ${r.subcooling.value.toFixed(1)}${DEG}F
                            </p>
                            <p class="tool-readout-detail">
                                Cond sat temp:
                                ${r.condSatTemp.toFixed(1)}${DEG}F
                            </p>
                            <p class="tool-status" data-status=${
                                r.subcooling.status === 'normal' ?
                                    'compliant' :
                                    'exceeds'
                            }>
                                ${STATUS_LABELS[r.subcooling.status]}
                            </p>
                            <p class="tool-readout-detail">
                                ${r.subcooling.recommendation}
                            </p>
                        </div>
                    </div>
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
